import { useEffect, useState } from "react";
import { isEmpty, isObject } from "lodash";
import { t } from "@/i18n-js/instance";
import { useLandingPageSettingsApi } from "@/react/hooks/home_page/useLandingPageSettingsApi";
import { CustomizeUI } from "@circle-react-shared/CustomizeUI";
import { FullScreenModal } from "@circle-react-shared/ModalsV2/FullScreenModal";
import { Loader } from "@circle-react-uikit/Loader";
import { useToast } from "@circle-react-uikit/ToastV2";
import { CustomizeHomeSectionSelector } from "./CustomizeHomeSectionSelector";
import { EditHomeForm } from "./EditHome/EditHomeForm";
import { FormFooter } from "./EditHome/FormFooter";
import {
  mapFormValuesToSettings,
  mapSettingsToDefaultFromValues,
} from "./EditHome/helpers";
import { HomeCustomizeUIHeader } from "./HomeCustomizeUIHeader";

const sections = [t("home_page.banners"), t("home_page.options")];
const fieldNames = ["logged_in", "logged_out"];

export interface CustomizeHomeProps {
  shouldShowCustomizeModal: boolean;
  toggleCustomizeModal: () => void;
  initialSection?: string;
}

export const CustomizeHome = ({
  shouldShowCustomizeModal,
  toggleCustomizeModal,
  initialSection = sections[0],
}: CustomizeHomeProps) => {
  const { error } = useToast();
  const [isPristine, setIsPristine] = useState(true);

  const {
    dataLoaded: hasDataLoaded,
    settings,
    metaData,
    fetchLandingPageSettings,
    updateLandingPageSettings,
    isSaving,
  } = useLandingPageSettingsApi();

  useEffect(() => {
    shouldShowCustomizeModal && fetchLandingPageSettings();
  }, [shouldShowCustomizeModal]);

  const [initialFormValues, setInitialFormValues] = useState({});

  useEffect(() => {
    hasDataLoaded &&
      setInitialFormValues(mapSettingsToDefaultFromValues(settings));
  }, [hasDataLoaded]);

  const onSubmit = async (formData: any) => {
    if (isPristine) return;

    let isValidForm = true;

    fieldNames.forEach(fieldName => {
      if (formData[`${fieldName}_banner_enabled`]) {
        const isMediaAdded = isObject(formData[`${fieldName}_media`])
          ? Boolean(formData[`${fieldName}_media`]?.url)
          : Boolean(formData[`${fieldName}_media`]);

        const isFieldAdded =
          Boolean(formData[`${fieldName}_title`]) ||
          Boolean(formData[`${fieldName}_description`]) ||
          isMediaAdded;

        isValidForm = isValidForm && Boolean(isFieldAdded);
      }
    });

    if (isValidForm) {
      const updatedSettings = mapFormValuesToSettings(formData, settings);
      await updateLandingPageSettings(updatedSettings);
      setInitialFormValues(formData);
      setIsPristine(true);
      return;
    }

    error(
      t("landing_page.settings.welcome_banner.errors.blank_welcome_banner"),
    );
  };

  const onCancel = () => {
    setIsPristine(true);
    toggleCustomizeModal();
  };

  return (
    <FullScreenModal
      isOpen={shouldShowCustomizeModal}
      onClose={toggleCustomizeModal}
      contentPortalId="customize-home-portal"
      shouldShowCloseButton
    >
      <CustomizeUI
        formId="edit-home-form"
        sections={sections}
        initialSection={initialSection}
        title={t("feed")}
        onExit={onCancel}
        onSave={onSubmit}
        isSaving={isSaving}
      >
        <HomeCustomizeUIHeader />

        <FullScreenModal.Body>
          {hasDataLoaded && settings && !isEmpty(initialFormValues) ? (
            <EditHomeForm
              settings={settings}
              onSubmit={onSubmit}
              onCancel={onCancel}
              initialFormValues={initialFormValues}
            >
              <CustomizeHomeSectionSelector
                metaData={metaData}
                initialFormValues={initialFormValues}
                setIsPristine={setIsPristine}
              />
            </EditHomeForm>
          ) : (
            <Loader center />
          )}
        </FullScreenModal.Body>
        <FullScreenModal.Footer>
          <FormFooter isPristine={isPristine} />
        </FullScreenModal.Footer>
      </CustomizeUI>
    </FullScreenModal>
  );
};
